import { useMentionCards } from "@/contexts/MentionCardsContext";
import { Separator } from "@components/ui/separator";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@components/ui/breadcrumb";
import { isBefore } from "date-fns";
import {
  DownloadIcon,
  InfoIcon,
  ListFilterIcon,
  MailIcon,
  MoreHorizontalIcon,
  SendIcon,
  ShareIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  generatePath,
  Link,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import {
  getSearchFilterOptions,
  hideIrrelevantMentions,
} from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { URLS } from "@/api/urls";
import { Button } from "@/components/ui/button";
import Initializing from "@/containers/Initializing";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { useUser } from "@/hooks/useUser";
import { RELATIVE_PATHS } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { stringifyUrl } from "@/utils";
import QuickSubscribe from "@/views/MentionsView/partials/QuickSubscribe";
import AllEarsTitle from "@components/AllEarsTitle";
import Container from "@components/Container";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";

import { useOrganization } from "@/hooks/useOrganization";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@components/ui/alert";
import { Trans } from "@lingui/react/macro";
import SortButton from "../partials/SortButton";
import TopWidgets from "../partials/TopWidgetsNew";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";
import { Spinner } from "@components/ui/spinner";
import SearchExportDialog from "../partials/SearchExportDialog";
import SearchShareDialog from "../partials/SearchShareDialog";
import SearchFilter from "@components/SearchFilter";
import SearchFilterBar from "@components/SearchFilterBar";
import { useFilterOrder } from "@components/SearchFilterBar/filterOrder";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import DialogReach from "@components/SearchFilter/partials/DialogReach";
import DialogFreeText from "@components/SearchFilter/partials/DialogFreeText";
import SearchDates from "@components/SearchDates";

type MentionLayoutProps = { children: React.ReactNode; url: string };

const MentionLayout: React.FunctionComponent<MentionLayoutProps> = ({
  children,
  url,
}) => {
  // URL state
  const { orgUuid, uuid } = useParams<{ orgUuid: string; uuid: string }>();
  const [filters, setFilters] = useFilterParamsV4();
  const history = useHistory();

  const isInsightsPage =
    useRouteMatch(`${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`)?.isExact || false;

  // Context
  const { alert, meta } = useMentionCards();

  // Local state
  const [filterOrder, handleFilterChange] = useFilterOrder(
    filters,
    setFilters,
    alert?.start_date
  );
  const [isFreeTextOpen, setIsFreeTextOpen] = useState<boolean>(false);
  const [isReachOpen, setIsReachOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);

  // Server state
  const { data: user } = useUser();
  const { id: user_id = "" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { plan } = orgData?.ok ? orgData.data : {};
  const allowedIntervals =
    plan?.notification_subscription_intervals || undefined;

  const {
    data: searchFilterOptionsData,
    error: searchFilterOptionsError, // TODO: handle this error
    isLoading: isSearchFilterOptionsLoading, // TODO: Handle this loading
  } = useQuery({
    queryKey: ["searches", uuid, "filter-options"],
    queryFn: () => getSearchFilterOptions(uuid),
  });

  // Side-effects
  const isInitializing = alert?.is_initializing;

  useEffect(() => {
    if (!isInitializing) return;

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;

    const checkIfSearchHasFinishedLoading = async () => {
      try {
        const response = await fetch(
          generatePath(URLS.SEARCHES_DETAIL, { uuid })
        );
        const data = await response.json();

        if (cancelled) return;

        if (data?.is_initializing === false) {
          window.location.reload();
        } else {
          timeout = setTimeout(checkIfSearchHasFinishedLoading, 10000); // Poll with regular intervals
        }
      } catch {
        // Do nothing on error
      }
    };

    checkIfSearchHasFinishedLoading();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isInitializing, uuid]);

  // Helpers
  const archived = alert
    ? alert.end_date && isBefore(new Date(alert.end_date), new Date())
    : false;

  return (
    <>
      <AllEarsTitle title={alert?.title} />
      {!alert ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : !alert?.can_mentions_be_presented ? (
        <Initializing alert={alert} />
      ) : (
        <>
          <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
            <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-350">
              <SidebarTrigger className="-ml-1" />

              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />

              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <Trans>Bevakningar</Trans>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{alert?.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <div className="flex items-center gap-2 ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        className="md:hidden"
                        size="icon"
                        variant="ghost"
                      />
                    }
                  >
                    <MoreHorizontalIcon />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => {
                        setIsSharing(true);
                      }}
                    >
                      <ShareIcon />
                      {isInsightsPage ? (
                        <Trans>Dela analys</Trans>
                      ) : (
                        <Trans>Dela</Trans>
                      )}
                    </DropdownMenuItem>
                    {orgUuid && (
                      <DropdownMenuItem
                        render={
                          <Link
                            to={generatePath(
                              `${url}${RELATIVE_PATHS.ALERT_SETTINGS}/`,
                              {
                                uuid,
                                orgUuid,
                              }
                            )}
                          />
                        }
                      >
                        <MailIcon />
                        <Trans>Prenumerera</Trans>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  data-testid="open-share-modal"
                  className="hidden md:flex"
                  onClick={() => {
                    postTrackingData(EVENT.SHARE_ALERT);
                    setIsSharing(true);
                  }}
                  variant="outline"
                  size="icon"
                >
                  <SendIcon />
                  <span className="sr-only">
                    <Trans>Dela</Trans>
                  </span>
                </Button>

                <Button
                  data-testid="open-export-modal"
                  onClick={() => {
                    postTrackingData(EVENT.EXPORT_ALERT);
                    setIsExporting(true);
                  }}
                  variant="outline"
                >
                  <Trans>Exportera</Trans>
                  <DownloadIcon />
                </Button>

                {!archived && user_id && orgUuid && (
                  <QuickSubscribe
                    className="hidden md:flex"
                    user_id={user_id}
                    organization_uuid={orgUuid}
                    search_uuid={uuid}
                    allowedIntervals={allowedIntervals}
                  />
                )}
              </div>
            </div>
          </header>

          <div className="border-b">
            <div className="w-full p-4 lg:p-6 max-w-350 flex justify-between items-end">
              <div className="flex flex-col gap-2.5">
                <h1
                  data-testid="mentions-title"
                  className="text-2xl font-bold w-full line-clamp-1"
                >
                  {alert?.title}
                </h1>

                {isInitializing ? (
                  <div className="flex items-center gap-2">
                    <Spinner />
                    <p className="text-muted-foreground">
                      <Trans>Söker efter historiska omnämnanden.</Trans>
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-3">
                <SearchDates
                  searchStartDate={alert?.start_date}
                  onChange={handleFilterChange}
                  selectedEndDate={filters.end_date}
                  selectedStartDate={filters.start_date}
                  minStartDate={
                    searchFilterOptionsData?.ok
                      ? searchFilterOptionsData.data.min_start_date
                      : undefined
                  }
                  maxEndDate={
                    searchFilterOptionsData?.ok
                      ? searchFilterOptionsData.data.max_end_date
                      : undefined
                  }
                />

                {searchFilterOptionsData ? (
                  searchFilterOptionsData.ok ? (
                    <SearchFilter
                      filterOptions={searchFilterOptionsData.data}
                      setIsFreeTextOpen={setIsFreeTextOpen}
                      setIsReachOpen={setIsReachOpen}
                      onChange={handleFilterChange}
                      value={filters}
                    >
                      <Button data-testid="filter-button">
                        <ListFilterIcon />
                        <Trans>Filter</Trans>
                      </Button>
                    </SearchFilter>
                  ) : (
                    <p className="text-destructive text-sm">
                      <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                    </p>
                  )
                ) : isSearchFilterOptionsLoading ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={<Button className="cursor-progress" />}
                    >
                      <ListFilterIcon />
                      <Trans>Filter</Trans>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Trans>Laddar in filter</Trans>
                    </TooltipContent>
                  </Tooltip>
                ) : searchFilterOptionsError ? (
                  <p className="text-destructive text-sm">
                    <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                  </p>
                ) : null}
              </div>
            </div>

            {searchFilterOptionsData ? (
              searchFilterOptionsData.ok ? (
                <SearchFilterBar
                  filterOptions={searchFilterOptionsData.data}
                  setIsFreeTextOpen={setIsFreeTextOpen}
                  setIsReachOpen={setIsReachOpen}
                  onChange={handleFilterChange}
                  filterOrder={filterOrder}
                  className="border-t"
                  value={filters}
                />
              ) : null
            ) : isSearchFilterOptionsLoading && filterOrder.length > 0 ? (
              <Skeleton className="h-11 w-full rounded-none" />
            ) : searchFilterOptionsError ? null : null}
          </div>

          {!isInitializing && (
            <>
              <TopWidgets
                searchUuid={alert.uuid}
                orgUuid={orgUuid}
                filters={filters}
              />

              <Tabs
                value={isInsightsPage ? "insights" : "mentions"}
                defaultValue="mentions"
                onValueChange={() => {
                  postTrackingData(EVENT.TOGGLE_ALERT_ANALYTICS, {
                    to: isInsightsPage ? "alert" : "analytics",
                  });

                  if (isInsightsPage) {
                    history.replace(
                      stringifyUrl({
                        url: generatePath(url, {
                          uuid: alert.uuid,
                          orgUuid,
                        }),
                        query: filters,
                      })
                    );
                  } else {
                    history.replace(
                      stringifyUrl({
                        url: generatePath(
                          `${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`,
                          {
                            orgUuid,
                            uuid: alert.uuid,
                          }
                        ),
                        query: filters,
                      })
                    );
                  }
                }}
              >
                <div className="relative">
                  <TabsList className="px-6 w-full">
                    <TabsTrigger value="mentions">
                      <Trans>Omnämnanden</Trans>
                    </TabsTrigger>

                    <TabsTrigger value="insights" data-testid="insights-toggle">
                      <Trans>Analys</Trans>
                    </TabsTrigger>
                  </TabsList>

                  <div className="absolute z-0 inset-0 pointer-events-none max-w-350 flex justify-end px-6">
                    <div className="pointer-events-auto">
                      <SortButton />
                    </div>
                  </div>
                </div>
              </Tabs>

              {meta?.llm_irrelevant_mentions_count ? (
                <Container>
                  <Alert>
                    <InfoIcon />
                    <AlertTitle>
                      {meta?.llm_irrelevant_mentions_count}{" "}
                      <Trans>omnämnanden med låg relevans</Trans>
                    </AlertTitle>
                    <AlertDescription>
                      <Trans>Bedömt utifrån frågan</Trans>: &quot;
                      {alert?.information_need}
                      &quot;
                    </AlertDescription>
                    <AlertAction>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={async () => {
                          const res = await hideIrrelevantMentions(uuid);
                          if (res.ok) window.location.reload();
                        }}
                      >
                        <Trans>Dölj alla</Trans>
                      </Button>
                    </AlertAction>
                  </Alert>
                </Container>
              ) : null}

              <div className="pt-4">{children}</div>
            </>
          )}
        </>
      )}

      <SearchShareDialog
        search={alert}
        open={isSharing}
        filters={filters}
        onOpenChange={setIsSharing}
      />

      <SearchExportDialog
        search={alert}
        filters={filters}
        open={isExporting}
        onOpenChange={setIsExporting}
      />

      {searchFilterOptionsData?.ok && (
        <>
          <DialogReach
            maxReach={searchFilterOptionsData.data.filter_max_reach_value}
            value={filters.min_reach_threshold}
            onOpenChange={setIsReachOpen}
            onChange={handleFilterChange}
            open={isReachOpen}
          />

          <DialogFreeText
            onOpenChange={setIsFreeTextOpen}
            onChange={handleFilterChange}
            value={filters.freetext}
            open={isFreeTextOpen}
          />
        </>
      )}
    </>
  );
};

export default MentionLayout;
